import React, { useState, useEffect } from 'react';
import { siteSettingService, mediaService } from '../../services/api';

const SETTING_KEYS = [
  'site_name', 'site_tagline', 'site_logo', 'site_favicon', 'site_url',
  'meta_title', 'meta_description', 'meta_keywords',
  'primary_color', 'secondary_color',
  'contact_email', 'contact_phone', 'contact_address',
  'copyright_text',
  'payment_upi_enabled', 'payment_card_enabled', 'payment_cod_enabled',
  'social_facebook', 'social_twitter', 'social_instagram', 'social_linkedin', 'social_youtube',
  'mail_mailer', 'mail_host', 'mail_port', 'mail_username', 'mail_password', 'mail_encryption', 'mail_from_address', 'mail_from_name',
];

const DEFAULT_VALUES = {
  site_name: 'Ortus',
  site_tagline: 'For the Fast Moving Consumer Generation',
  site_logo: '',
  site_favicon: '',
  site_url: 'https://www.ortusfnb.com',
  meta_title: 'Ortus FMCG Limited',
  meta_description: '',
  meta_keywords: '',
  primary_color: '#059669',
  secondary_color: '#0f172a',
  contact_email: '',
  contact_phone: '',
  contact_address: '',
  copyright_text: 'Ortus FMCG Limited',
  social_facebook: '',
  social_twitter: '',
  social_instagram: '',
  social_linkedin: '',
  social_youtube: '',
  payment_upi_enabled: '1',
  payment_card_enabled: '1',
  payment_cod_enabled: '1',
  mail_mailer: 'smtp',
  mail_host: '',
  mail_port: '587',
  mail_username: '',
  mail_password: '',
  mail_encryption: 'tls',
  mail_from_address: '',
  mail_from_name: '',
};

function SettingsManagement() {
  const [settings, setSettings] = useState({ ...DEFAULT_VALUES });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({});
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const data = await siteSettingService.index();
      const list = Array.isArray(data) ? data : data?.data || [];
      const map = { ...DEFAULT_VALUES };
      list.forEach((s) => { if (s.key) map[s.key] = s.value || ''; });
      setSettings(map);
    } catch (e) {
      console.error('Error loading settings:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const resolveMediaUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    const base = process.env.REACT_APP_API_URL?.replace('/api/v1', '') || 'https://api.ortusfnb.com';
    return base + (path.startsWith('/public') ? path : '/public' + path);
  };

  const handleUpload = async (file, key) => {
    if (!file) return;
    setUploading((prev) => ({ ...prev, [key]: true }));
    try {
      const media = await mediaService.upload(file, 'site');
      const url = resolveMediaUrl(media.url || media.path || '');
      handleChange(key, url);
      setMessage(`${key.replace('site_', '')} uploaded successfully`);
      setTimeout(() => setMessage(''), 3000);
    } catch (e) {
      alert('Upload failed: ' + (e.response?.data?.message || e.message));
    }
    setUploading((prev) => ({ ...prev, [key]: false }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = SETTING_KEYS.map((key) => ({
        key,
        value: settings[key] || '',
        group: key.startsWith('meta_') ? 'seo' : key.startsWith('social_') ? 'social' : key.startsWith('contact_') ? 'contact' : key.startsWith('primary') || key.startsWith('secondary') ? 'theme' : key.startsWith('mail_') ? 'email' : 'general',
        label: key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
        type: key === 'site_logo' || key === 'site_favicon' ? 'image' : key === 'meta_description' || key === 'contact_address' ? 'textarea' : 'text',
      }));
      await siteSettingService.bulkUpdate(payload);
      setMessage('Settings saved successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (e) {
      alert('Error saving settings: ' + (e.response?.data?.message || e.message));
    }
    setSaving(false);
  };

  if (loading) return <div className="text-slate-300">Loading settings...</div>;

  const Input = ({ label, keyName, type = 'text', placeholder = '' }) => (
    <div className="space-y-1">
      <label className="text-[10px] uppercase text-slate-500 font-semibold tracking-wider">{label}</label>
      {type === 'textarea' ? (
        <textarea
          value={settings[keyName] || ''}
          onChange={(e) => handleChange(keyName, e.target.value)}
          placeholder={placeholder}
          rows={3}
          className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-red-500 resize-none"
        />
      ) : (
        <input
          type={type}
          value={settings[keyName] || ''}
          onChange={(e) => handleChange(keyName, e.target.value)}
          placeholder={placeholder}
          className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-red-500"
        />
      )}
    </div>
  );

  const ToggleField = ({ label, keyName, helper = '' }) => (
    <div className="flex items-center justify-between border border-slate-800 rounded-2xl px-4 py-3">
      <div>
        <p className="text-sm font-semibold text-slate-100">{label}</p>
        {helper && <p className="text-xs text-slate-500">{helper}</p>}
      </div>
      <button
        type="button"
        onClick={() => handleChange(keyName, settings[keyName] === '1' ? '0' : '1')}
        className={`relative inline-flex h-6 w-12 items-center rounded-full transition ${settings[keyName] === '1' ? 'bg-red-500' : 'bg-slate-700'}`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${settings[keyName] === '1' ? 'translate-x-6' : 'translate-x-1'}`}
        />
      </button>
    </div>
  );

  const MediaField = ({ label, keyName }) => (
    <div className="space-y-2">
      <label className="text-[10px] uppercase text-slate-500 font-semibold tracking-wider">{label}</label>
      <div className="flex items-center gap-3">
        <input
          value={settings[keyName] || ''}
          onChange={(e) => handleChange(keyName, e.target.value)}
          placeholder="URL or upload"
          className="flex-1 bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-red-500"
        />
        <label className="cursor-pointer">
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => handleUpload(e.target.files[0], keyName)}
          />
          <span className={`inline-flex items-center px-3 py-2 rounded-lg text-xs font-medium transition ${uploading[keyName] ? 'bg-slate-700 text-slate-400' : 'bg-slate-800 text-slate-300 hover:bg-slate-700 border border-slate-700'}`}>
            {uploading[keyName] ? 'Uploading...' : 'Upload'}
          </span>
        </label>
      </div>
      {settings[keyName] && (
        <div className="mt-2">
          <img src={settings[keyName]} alt={label} className="h-16 w-auto rounded-lg border border-slate-800 object-contain bg-slate-950" />
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-slate-100">Site Profile</h2>
        {message && (
          <span className={`text-sm px-3 py-1 rounded-full ${message.includes('Error') ? 'bg-red-600/10 text-red-400' : 'bg-red-600/10 text-red-400'}`}>
            {message}
          </span>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Site Profile */}
        <section className="bg-slate-900 rounded-2xl border border-slate-800 p-6 space-y-5">
          <h3 className="text-sm font-bold text-slate-300 uppercase tracking-wider border-b border-slate-800 pb-2">Site Profile</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Input label="Site Name" keyName="site_name" />
            <Input label="Tagline" keyName="site_tagline" />
            <Input label="Site URL (Frontend)" keyName="site_url" placeholder="https://www.ortusfnb.com" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <MediaField label="Site Logo" keyName="site_logo" />
            <MediaField label="Site Favicon" keyName="site_favicon" />
          </div>
        </section>

        {/* Payment Methods */}
        <section className="bg-slate-900 rounded-2xl border border-slate-800 p-6 space-y-5">
          <h3 className="text-sm font-bold text-slate-300 uppercase tracking-wider border-b border-slate-800 pb-2">Payment Methods</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ToggleField label="Enable UPI" keyName="payment_upi_enabled" helper="Allow QR / UPI payments at checkout" />
            <ToggleField label="Enable Cards" keyName="payment_card_enabled" helper="Allow credit & debit card payments" />
            <ToggleField label="Enable Cash on Delivery" keyName="payment_cod_enabled" helper="Allow COD for eligible orders" />
          </div>
        </section>

        {/* Meta Tags */}
        <section className="bg-slate-900 rounded-2xl border border-slate-800 p-6 space-y-5">
          <h3 className="text-sm font-bold text-slate-300 uppercase tracking-wider border-b border-slate-800 pb-2">Meta Tags (SEO)</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Input label="Meta Title" keyName="meta_title" />
            <Input label="Meta Keywords" keyName="meta_keywords" />
          </div>
          <Input label="Meta Description" keyName="meta_description" type="textarea" />
        </section>

        {/* Theme */}
        <section className="bg-slate-900 rounded-2xl border border-slate-800 p-6 space-y-5">
          <h3 className="text-sm font-bold text-slate-300 uppercase tracking-wider border-b border-slate-800 pb-2">Theme Colors</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-1">
              <label className="text-[10px] uppercase text-slate-500 font-semibold tracking-wider">Primary Color</label>
              <div className="flex items-center gap-3">
                <input type="color" value={settings.primary_color || '#059669'} onChange={(e) => handleChange('primary_color', e.target.value)} className="h-10 w-14 rounded-lg border border-slate-700 bg-slate-950 cursor-pointer" />
                <input value={settings.primary_color || ''} onChange={(e) => handleChange('primary_color', e.target.value)} className="flex-1 bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 font-mono" />
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-[10px] uppercase text-slate-500 font-semibold tracking-wider">Secondary Color</label>
              <div className="flex items-center gap-3">
                <input type="color" value={settings.secondary_color || '#0f172a'} onChange={(e) => handleChange('secondary_color', e.target.value)} className="h-10 w-14 rounded-lg border border-slate-700 bg-slate-950 cursor-pointer" />
                <input value={settings.secondary_color || ''} onChange={(e) => handleChange('secondary_color', e.target.value)} className="flex-1 bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 font-mono" />
              </div>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="bg-slate-900 rounded-2xl border border-slate-800 p-6 space-y-5">
          <h3 className="text-sm font-bold text-slate-300 uppercase tracking-wider border-b border-slate-800 pb-2">Contact Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Input label="Email" keyName="contact_email" type="email" />
            <Input label="Phone" keyName="contact_phone" />
          </div>
          <Input label="Office Address" keyName="contact_address" type="textarea" />
          <Input label="Copyright Text" keyName="copyright_text" />
        </section>

        {/* Social */}
        <section className="bg-slate-900 rounded-2xl border border-slate-800 p-6 space-y-5">
          <h3 className="text-sm font-bold text-slate-300 uppercase tracking-wider border-b border-slate-800 pb-2">Social Links</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Input label="Facebook" keyName="social_facebook" placeholder="https://facebook.com/..." />
            <Input label="Twitter / X" keyName="social_twitter" placeholder="https://twitter.com/..." />
            <Input label="Instagram" keyName="social_instagram" placeholder="https://instagram.com/..." />
            <Input label="LinkedIn" keyName="social_linkedin" placeholder="https://linkedin.com/..." />
            <Input label="YouTube" keyName="social_youtube" placeholder="https://youtube.com/..." />
          </div>
        </section>

        {/* Email Configuration */}
        <section className="bg-slate-900 rounded-2xl border border-slate-800 p-6 space-y-5">
          <h3 className="text-sm font-bold text-slate-300 uppercase tracking-wider border-b border-slate-800 pb-2">Email Configuration (SMTP)</h3>
          <p className="text-xs text-slate-500">Configure SMTP to send welcome emails, order confirmations, password resets and more.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Input label="Mailer" keyName="mail_mailer" placeholder="smtp" />
            <Input label="Host" keyName="mail_host" placeholder="smtp.gmail.com" />
            <Input label="Port" keyName="mail_port" placeholder="587" />
            <Input label="Encryption" keyName="mail_encryption" placeholder="tls / ssl" />
            <Input label="Username" keyName="mail_username" placeholder="your@email.com" />
            <Input label="Password" keyName="mail_password" type="password" placeholder="App password / SMTP password" />
            <Input label="From Address" keyName="mail_from_address" placeholder="noreply@yourdomain.com" />
            <Input label="From Name" keyName="mail_from_name" placeholder="Ortus FMCG" />
          </div>
        </section>

        <div className="flex gap-3 pt-2">
          <button
            type="submit"
            disabled={saving}
            className="bg-red-600 text-white px-6 py-2.5 rounded-lg text-sm font-semibold hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save All Settings'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default SettingsManagement;
